"use client";

import type { BlockProps } from "./blocks/registry";
import { Heading } from "../design-system/atoms/Heading";
import Icon from "../design-system/atoms/Icon";
import { useMobileSlider } from "../hooks/useMobileSlider";

type StrapiMedia = {
  url: string;
};

type ChoixMenuItem = {
  id: number | string;
  titre?: string;
  descr: string;
  iconAlt?: string;
  icon: StrapiMedia;
};

type PourquoiLMSData = {
  id?: number | string;
  documentId?: string;
  __component?: string;
  title: string;
  variant?: string;
  choixMenus: ChoixMenuItem[];
  [key: string]: unknown;
};

export default function PourquoiLMS({ data }: BlockProps<PourquoiLMSData>) {
  const choixMenus = data?.choixMenus ?? [];

  const { sliderRef, activeIndex, handleScroll, scrollToIndex } =
    useMobileSlider({
      itemsLength: choixMenus.length,
    });

  if (!data) return null;

  if (data.variant === "prescripteur") {
    return (
      <section className="py-24 bg-white overflow-hidden">
        <div className="lms-container text-center min-w-0">
          <Heading
            as="h2"
            className="text-3xl lg:text-5xl font-bold mb-16 text-center text-text"
          >
            {(() => {
              const words = data.title.split(" ");
              const lastThree = words.slice(-4).join(" ");
              const firstPart = words.slice(0, -4).join(" ");

              return (
                <>
                  {firstPart}{" "}
                  <span className="text-secondary font-semibold">
                    {lastThree}
                  </span>
                </>
              );
            })()}
          </Heading>

          {/* Slider mobile + grille desktop */}
          <div className="w-full max-w-full min-w-0 overflow-hidden sm:overflow-visible">
            <div
              ref={sliderRef}
              onScroll={handleScroll}
              className="
                flex w-full max-w-full min-w-0 overflow-x-auto scroll-smooth snap-x snap-mandatory pt-14
                sm:grid sm:grid-cols-2 sm:gap-10 sm:overflow-visible sm:snap-none
                lg:grid-cols-4 lg:gap-12
                [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
              "
            >
              {choixMenus.map((item) => (
                <div
                  key={item.id}
                  className="
                    basis-full min-w-0 shrink-0 snap-center flex justify-center px-2
                    sm:basis-auto sm:shrink sm:snap-none sm:px-0 sm:w-full
                  "
                >
                  <div
                    className="
                      w-full max-w-[260px]
                      flex flex-col items-center text-center space-y-4 p-5
                      hover:rounded-2xl hover:border-gray-200
                      hover:shadow-[0px_8px_30px_rgba(0,0,0,0.1)]
                      sm:max-w-none
                    "
                  >
                    <div
                      className="
                        relative flex items-center justify-center w-20 h-20
                        transition-transform duration-300 hover:scale-110
                      "
                    >
                      <Icon
                        src={item.icon.url}
                        size={30}
                        alt={item.iconAlt || "Icon"}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <div className="w-20 h-[4px] bg-primaryforeground rounded-full" />

                    <p className="text-gray-700 text-base lg:text-lg leading-snug max-w-[250px]">
                      {item.descr}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dots mobile */}
          {choixMenus.length > 1 && (
            <div className="flex justify-center gap-2 mt-6 sm:hidden">
              {choixMenus.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => scrollToIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    activeIndex === index
                      ? "w-6 bg-secondary"
                      : "w-2 bg-gray-300"
                  }`}
                  aria-label={`Aller au choix ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      key={data.id}
      className="
        bg-accent lg:min-h-[65vh] lg:mt-[-200px]
        flex justify-center items-center overflow-hidden
      "
    >
      <div className="lms-container pt-5 pb-8 lg:pt-24 flex flex-col items-center min-w-0">
        <Heading
          as="h2"
          className="text-4xl lg:text-5xl text-center font-bold text-text pt-6 2xl:pt-20 3xl:pt-10"
        >
          {data.title ? (
            <>
              {data.title.split(" ").slice(0, -3).join(" ")}{" "}
              <span className="text-white">
                {data.title.split(" ").slice(-3).join(" ")}
              </span>
            </>
          ) : (
            "Pourquoi choisir Les Menus Services ?"
          )}
        </Heading>

        {/* Slider mobile + flex desktop */}
        <div className="w-full max-w-full min-w-0 overflow-hidden lg:overflow-visible mt-6">
          <div
            ref={sliderRef}
            onScroll={handleScroll}
            className="
              flex w-full max-w-full min-w-0 overflow-x-auto scroll-smooth snap-x snap-mandatory
              lg:flex-row lg:gap-6 lg:overflow-visible lg:snap-none
              max-w-7xl
              [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
            "
          >
            {choixMenus.map((card) => (
              <div
                key={card.id}
                className="
                  basis-full min-w-0 shrink-0 snap-center flex justify-center px-2
                  lg:basis-0 lg:flex-1 lg:shrink lg:snap-none lg:px-0
                "
              >
                <article
                  className="
                    w-full max-w-[320px] bg-white rounded-2xl px-4 py-6 text-center transition shadow-lg
                    lg:max-w-none
                  "
                  style={{ boxShadow: "0px 0px 29.8px rgba(0,0,0,0.15)" }}
                >
                  <Icon
                    src={card.icon.url}
                    alt={card.iconAlt ?? card.descr}
                    className="mx-auto h-20 w-20 object-contain"
                  />

                  {card.titre && (
                    <h3 className="mt-6 text-2xl font-semibold text-primaryGreen">
                      {card.titre}
                    </h3>
                  )}

                  <p className="mt-4 text-base leading-relaxed text-text">
                    {card.descr}
                  </p>
                </article>
              </div>
            ))}
          </div>
        </div>

        {/* Dots mobile */}
        {choixMenus.length > 1 && (
          <div className="flex justify-center gap-2 mt-6 lg:hidden">
            {choixMenus.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => scrollToIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index
                    ? "w-6 bg-white"
                    : "w-2 bg-white/50"
                }`}
                aria-label={`Aller au choix ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}